/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { IClRegionDataState } from "../../Interfaces/SiteContent";
import {
  IGetClRegionsFailure,
  IGetClRegionsSuccess,
  IGetGeoDataFailure,
  IGetGeoDataSuccess,
} from "./ClRegionAction";
import {
  CL_REGION_LOAD_FAILURE,
  CL_REGION_LOAD_SUCCESS,
  GEO_DATA_LOAD_FAILURE,
  GEO_DATA_LOAD_SUCCESS,
} from "./ClRegionActionTypes";

const initialState: IClRegionDataState = {
  clRegions: [],
  clRegionsOptionList: [],
  clRegionsLoadError: "",
  geoData: [],
  geoDataLoadError: "",
};

/**
 * Updates state of fetch cl regions success.
 * @param {IClRegionDataState} state The cl regions state.
 * @param {IGetClRegionsSuccess} action The cl regions action success.
 * @returns {IClRegionDataState} New cl regions state.
 */
const applyLoadClRegions = (
  state: IClRegionDataState,
  action: IGetClRegionsSuccess
): IClRegionDataState => ({
  ...state,
  clRegions: action.regions,
  clRegionsOptionList: action.regions.map((x) => ({
    key: x.clRegionName,
    text: x.clRegionName,
  })),
});

/**
 * Updates state of fetch cl regions failure.
 * @param {IClRegionDataState} state The cl regions state.
 * @param {IGetClRegionFailure} action The cl regions action failure.
 * @returns {IClRegionDataState} New cl regions state.
 */
const applyLoadFailClRegions = (
  state: IClRegionDataState,
  action: IGetClRegionsFailure
): IClRegionDataState => ({
  ...state,
  clRegionsLoadError: action.regionsLoadError,
});

/**
 * Updates state of fetch geo data success.
 * @param {IClRegionDataState} state The cl regions state.
 * @param {IGetClRegionsSuccess} action The cl regions action success.
 * @returns {IClRegionDataState} New cl regions state.
 */
const applyLoadGeoData = (
  state: IClRegionDataState,
  action: IGetGeoDataSuccess
): IClRegionDataState => ({
  ...state,
  geoData: action.geoData,
});

/**
 * Updates state of fetch geo data failure.
 * @param {IClRegionDataState} state The cl regions state.
 * @param {IGetClRegionFailure} action The cl regions action failure.
 * @returns {IClRegionDataState} New cl regions state.
 */
const applyLoadFailGeoData = (
  state: IClRegionDataState,
  action: IGetGeoDataFailure
): IClRegionDataState => ({
  ...state,
  geoDataLoadError: action.geoDataLoadError,
});

/**
 * Reducer function for Activity component.
 * @param {IClRegionDataState} state The activity state.
 * @param {ClRegionActionTypes} action The action type.
 * @returns {IClRegionDataState} The activity state.
 */

function ClRegionReducer(state: IClRegionDataState = initialState, action): IClRegionDataState {
  switch (action.type) {
    case CL_REGION_LOAD_SUCCESS:
      return applyLoadClRegions(state, action);
    case CL_REGION_LOAD_FAILURE:
      return applyLoadFailClRegions(state, action);
    case GEO_DATA_LOAD_SUCCESS:
      return applyLoadGeoData(state, action);
    case GEO_DATA_LOAD_FAILURE:
      return applyLoadFailGeoData(state, action);
    default:
      return state;
  }
}

export default ClRegionReducer;
